import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { fillArray } from 'lib/array/arrayUtils'
import useAttractionOffers from 'hooks/Experiences/useAttractionOffers'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperiencesSortOptions from '../hooks/useExperiencesSortOptions'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${rem(200)};
  position: relative;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

interface Props {
  attractionId: string
  searchFilters?: Experiences.SearchFilters
}

function ExperienceSearchAttractionResults({ attractionId, searchFilters }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { sortBy } = useExperiencesSortOptions()
  const { offerIds, fetching: fetchingOffers, error: attractionError } = useAttractionOffers(attractionId, searchFilters)

  const experiencesMap = useAppSelector(state => state.experience.experiences)
  const fetchingExperiencesMap = useAppSelector(state => state.experience.fetchingExperiences)
  const experienceErrorsMap = useAppSelector(state => state.experience.experienceErrors)

  const tracking = useOfferListTracking(getListName('OfferList', 'EXPERIENCES', 'Attraction Offers'), {
    source: 'attraction',
    key: attractionId,
  })

  useEffect(() => {
    if (offerIds.length > 0) {
      dispatch(fetchExperiencesById(...offerIds))
    }
  }, [offerIds, dispatch])

  const experiences = useMemo(() => {
    const loaded = offerIds.map(id => experiencesMap[id]).filter(Boolean)
    switch (sortBy) {
      case 'price.asc': return [...loaded].sort((a, b) => a.price - b.price)
      case 'price.desc': return [...loaded].sort((a, b) => b.price - a.price)
      case 'customerRating': return [...loaded].sort((a, b) => (b.rating?.score ?? 0) - (a.rating?.score ?? 0))
      case 'bestValue': return [...loaded].sort((a, b) => b.discount - a.discount)
      default: return loaded
    }
  }, [offerIds, experiencesMap, sortBy])

  const fetchingExperiences = useMemo(
    () => offerIds.some(id => fetchingExperiencesMap[id]),
    [offerIds, fetchingExperiencesMap],
  )

  const allExperiencesResolved = useMemo(
    () => offerIds.length > 0 && offerIds.every(id => experiencesMap[id] || experienceErrorsMap[id]),
    [offerIds, experiencesMap, experienceErrorsMap],
  )

  const hasError = !!attractionError
  const fetching = !hasError && (fetchingOffers || fetchingExperiences || (offerIds.length > 0 && !allExperiencesResolved))

  return (
    <VerticalSpacer gap={16}>
      {hasError && <BodyText variant="large" colour="neutral-two">
        {intl.formatMessage({
          id: 'qawEZZcZ5gPQ8RUSbqD9',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Error message shown when loading attraction offers fails',
        })}
      </BodyText>}
      {!hasError && !fetching && <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage(
            {
              id: '3srKgtYjSLwOR-04HAjP',
              defaultMessage: '{count, plural, one {# experience} other {# experiences}} found',
              description: 'Pluralized label showing the number of experiences returned in search results',
            },
            { count: experiences.length },
          )}
        </BodyText>
        <ExperienceSearchPageSort />
      </Group>}
      <OfferListEventsProvider tracking={tracking}>
        <ListGrid>
          {fetching && fillArray(4).map((id) => <ExperienceCompactSearchTileSkeleton key={id} />)}
          {!fetching && !hasError && experiences.map((experience, index) => (
            <ExperienceCompactSearchTile
              key={experience.id}
              experience={experience}
              position={index}
            />
          ))}
        </ListGrid>
      </OfferListEventsProvider>
    </VerticalSpacer>
  )
}

export default ExperienceSearchAttractionResults
